import React, { useMemo } from 'react';
import { Edge, Node } from 'reactflow';
import styled from 'styled-components';
import { EdgeData, NodeData } from '../../Charts/FlowChart/types';
import { themeColor } from '../../theme/colors';
import { formatPercentage } from '../../utils';
import Metric from '../..//Metric/Metric';
import MetricTrend from '../../Metric/MetricTrend';
import {
  ErrorBreakdown,
  FunctionMetrics,
} from '@hud/portal-dtos/webapp-function.dto';

const maxVisibleErrors = 2;

const callerTitle = 'When called from this caller';
const calleeTitle = 'This callee, when called from the function';
const flowCallerTitle = (flowType: string) => `When called from this ${flowType.toLocaleLowerCase()}`;

const callerExceptionsTitle = 'Exceptions Thrown:';
const calleeExceptionsTitle = 'Throws exceptions:';

export function EdgeInspector({ edge }: { edge: Edge<EdgeData> }) {
  const { title, errorsTitle, errors, metrics } = useMemo(() => {
    if (edge.data?.inspectorData?.type === 'CALLER') {
      return {
        title: `${callerTitle} (${formatPercentage(
          edge.data?.inspectorData?.payload?.metrics.distribution.value,
        )}):`,
        errorsTitle: callerExceptionsTitle,
        errors: edge.data?.inspectorData?.payload?.errors || [],
        metrics: edge.data?.inspectorData?.payload?.metrics as FunctionMetrics,
      };
    }

    if (edge.data?.inspectorData?.type === 'CALLEE') {
      return {
        title: `${calleeTitle} (${formatPercentage(
          edge.data.inspectorData.payload?.metrics.rate.value,
        )}):`,
        errorsTitle: calleeExceptionsTitle,
        errors: edge.data.inspectorData.payload?.errors || [],
        metrics: edge.data.inspectorData.payload?.metrics as FunctionMetrics,
      };
    }

    if (edge.data?.inspectorData?.type === 'FLOW') {
      const valueToMetric = (value: number | undefined) => {
        return value ? { value } : null;
      }
      return {
        title: `${flowCallerTitle(edge.data?.inspectorData?.payload?.type || 'FLOW')} (${formatPercentage(
          edge.data.inspectorData.payload?.distribution || 0,
        )}):`,
        errorsTitle: '',
        errors: [],
        metrics: {
          runs: valueToMetric(edge.data.inspectorData.payload?.runs),
          normalizedRuns: valueToMetric(edge.data.inspectorData.payload?.normalized_runs),
          durationAvg: valueToMetric(edge.data.inspectorData.payload?.avg_duration),
          durationP90: null,
          errorRate:  valueToMetric(edge.data.inspectorData.payload?.error_rate),
          cost: null,
        } as unknown as FunctionMetrics,
      };
    }

    return {
      title: '<unknown type>',
      errorsTitle: '',
      errors: [],
      metrics: {
        runs: null,
        normalizedRuns: null,
        durationAvg: null,
        durationP90: null,
        errorRate: null,
        cost: null,
      } as unknown as FunctionMetrics,
    };
  }, [edge.data]);

  if (!edge.data) {
    return <div>error</div>;
  }

  return (
    <BaseInspector
      title={title}
      errorsTitle={errorsTitle}
      errors={errors}
      metrics={metrics}
    />
  );
}

export function NodeInspector({ node }: { node: Node<NodeData> }) {
  if (!node.data.function) {
    return <div>error</div>;
  }

  const errorsTitle = calleeExceptionsTitle;

  const functionData = node.data.function;

  const errors = functionData.errors || [];
  const metrics = functionData.metrics as FunctionMetrics;

  return (
    <BaseInspector
      errorsTitle={errorsTitle}
      errors={errors}
      metrics={metrics}
    />
  );
}

function BaseInspector({
  title,
  errorsTitle,
  errors,
  metrics,
}: {
  title?: string;
  errorsTitle: string;
  errors: ErrorBreakdown;
  metrics: FunctionMetrics;
}) {
  const errorList = errors.slice(0, maxVisibleErrors) || [];
  const leftOver =
    errors.length > maxVisibleErrors ? errors.length - maxVisibleErrors : 0;

  return (
    <Container>
      {title && <Title>{title}</Title>}
      <Values>
        {metrics?.normalizedRuns !== null && (
          <>
            <div>Invocations:</div>
            <div>
              <RunsMetricContainer>
                <Metric
                  unit="abbreviated"
                  metric={metrics?.normalizedRuns}
                />
                <MetricTrend metric={metrics?.runs} variant="unstyled" />
              </RunsMetricContainer>
            </div>
          </>
        )}

        {metrics?.durationAvg !== null && (
          <>
            <div>Dur avg:</div>
            <div>
              <Metric
                unit="duration"
                metric={metrics?.durationAvg}
                showTrend
                trendVariant="unstyled"
              />
            </div>
          </>
        )}

        {metrics?.errorRate !== null && (
          <>
            <div>Err rate:</div>
            <div>
              <Metric
                unit="percent"
                metric={metrics?.errorRate}
                showTrend
                trendVariant="unstyled"
              />
            </div>
          </>
        )}
      </Values>

      {errorList.length > 0 && <Title>{errorsTitle}</Title>}
      {errorList.map((error) => (
        <Values key={error.name}>
          <div>{formatPercentage(error.metrics.distribution.value)}</div>
          <div>{error.name}</div>
        </Values>
      ))}
      {leftOver > 0 && (
        <Values>
          <div>+{leftOver} more</div>
        </Values>
      )}
    </Container>
  );
}

export const RunsMetricContainer = styled.span`
  display: flex;
  justify-content: center;
  gap: 7px;
`;

const Container = styled.div`
  color: ${themeColor('tooltipForeground')} !important;
  border: 1px ${themeColor('tooltipBorder')} solid;
  background-color: ${themeColor('tooltipBackground')} !important;

  z-index: 1000;
  border-radius: 4px;
  padding: 8px;
`;

const Title = styled.h5`
  padding: 0;
  margin: 0;
  font-weight: normal;
  font-size: 0.8em;
  opacity: 0.8;

  padding-bottom: 4px;
  &:not(:first-child) {
    padding-top: 7px;
  }
`;

const Values = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  justify-items: start;

  gap: 4px;

  & > div:nth-child(2n) {
    justify-self: end;
  }
`;
